import React, { useEffect, useState } from "react";
import { X, ArrowDown, Loader2, AlertCircle } from "lucide-react";
import { getShiftOrders, getShiftSummary } from "../../../api/shiftService";
import { getSalesOrderDetail } from "../../../api/salesService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function formatCurrency(n) {
  return `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
}

function normalizeTopProducts(rows = []) {
  return rows.map((row, idx) => ({
    id:
      row.id ||
      row.product_id ||
      row.item_id ||
      `${row.name || row.item_name || "item"}-${idx}`,
    name:
      row.name || row.item_name || row.product_name || row.service_name || "-",
    sold: Number(row.sold ?? row.qty ?? row.quantity ?? row.total_qty ?? 0),
    revenue: Number(
      row.revenue ?? row.total ?? row.total_revenue ?? row.total_price ?? 0,
    ),
  }));
}

export default function ShiftRecapModal({ onClose, shiftId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shiftId) {
      setLoading(false);
      setError("Shift aktif tidak ditemukan");
      return;
    }

    let cancelled = false;

    const deriveFromOrderDetails = async () => {
      const firstPage = await getShiftOrders(shiftId, {
        page: 1,
        limit: 100,
        status: "DONE",
      });
      const firstRows = firstPage?.data || [];
      const totalPages = Number(firstPage?.meta?.total_pages || 1);

      let allOrders = [...firstRows];
      if (totalPages > 1) {
        const restPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            getShiftOrders(shiftId, {
              page: i + 2,
              limit: 100,
              status: "DONE",
            }),
          ),
        );
        restPages.forEach((res) => {
          allOrders = allOrders.concat(res?.data || []);
        });
      }

      const details = await Promise.all(
        allOrders
          .filter((order) => order?.id)
          .map((order) =>
            getSalesOrderDetail(order.id)
              .then((res) => res?.data ?? res)
              .catch(() => null),
          ),
      );

      const agg = new Map();
      details.forEach((detail) => {
        (detail?.items || []).forEach((item) => {
          const name =
            item.item_name ||
            item.product_name ||
            item.service_name ||
            item.name ||
            "-";
          const qty = Number(item.quantity || item.qty || 0);
          const total = Number(
            item.total_price ??
              Number(item.unit_price || item.price || 0) * qty,
          );
          const key = `${name}`;

          if (!agg.has(key)) {
            agg.set(key, { id: item.id || key, name, sold: 0, revenue: 0 });
          }
          const curr = agg.get(key);
          curr.sold += qty;
          curr.revenue += total;
        });
      });

      return Array.from(agg.values())
        .sort((a, b) => b.sold - a.sold || b.revenue - a.revenue)
        .slice(0, 50);
    };

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const summaryRes = await getShiftSummary(shiftId);
        const summary = summaryRes?.data ?? summaryRes ?? {};

        const fromSummary = normalizeTopProducts(
          summary?.top_products ||
            summary?.best_selling_products ||
            summary?.product_sales ||
            [],
        ).filter((item) => item.name && (item.sold > 0 || item.revenue > 0));

        const finalItems =
          fromSummary.length > 0 ? fromSummary : await deriveFromOrderDetails();
        if (!cancelled) setItems(finalItems);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Gagal memuat rekap penjualan kasir");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [shiftId]);

  const handleDownload = () => {
    if (!items.length) return;

    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const now = new Date();
    const dateStr = now.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const timeStr = now.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // Header bar
    doc.setFillColor(15, 118, 110);
    doc.rect(0, 0, pageW, 22, "F");

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("REKAP PENJUALAN KASIR", pageW / 2, 14, { align: "center" });

    // Sub-info line
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80);
    doc.text(`Dicetak pada: ${dateStr}, ${timeStr}`, 14, 30);
    doc.text(`Total item: ${items.length}`, pageW - 14, 30, { align: "right" });

    // Divider
    doc.setDrawColor(220);
    doc.line(14, 33, pageW - 14, 33);

    doc.setTextColor(0);

    // Table
    autoTable(doc, {
      startY: 37,
      head: [["No", "Nama Produk", "Terjual", "Pendapatan"]],
      body: items.map((item, i) => [
        i + 1,
        item.name,
        Number(item.sold || 0).toLocaleString("id-ID"),
        `Rp ${Number(item.revenue || 0).toLocaleString("id-ID")}`,
      ]),
      headStyles: {
        fillColor: [15, 118, 110],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 10,
      },
      alternateRowStyles: { fillColor: [245, 250, 250] },
      styles: { fontSize: 10, cellPadding: 4, overflow: "linebreak" },
      columnStyles: {
        0: { halign: "center", cellWidth: 18 },
        2: { halign: "center", cellWidth: 28 },
        3: { halign: "right", cellWidth: 46 },
      },
    });

    // Footer total row
    const totalSold = items.reduce(
      (sum, item) => sum + Number(item.sold || 0),
      0,
    );
    const totalRevenue = items.reduce(
      (sum, item) => sum + Number(item.revenue || 0),
      0,
    );
    const finalY = doc.lastAutoTable.finalY;

    doc.setFillColor(240, 253, 250);
    doc.rect(14, finalY, pageW - 28, 10, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 118, 110);
    doc.text("TOTAL", 18, finalY + 7);
    doc.text(
      totalSold.toLocaleString("id-ID"),
      14 + 18 + (pageW - 28 - 18 - 28 - 46) / 2,
      finalY + 7,
      { align: "center" },
    );
    doc.text(
      `Rp ${totalRevenue.toLocaleString("id-ID")}`,
      pageW - 14,
      finalY + 7,
      { align: "right" },
    );

    doc.save(
      `rekap-penjualan-kasir-${new Date().toISOString().slice(0, 10)}.pdf`,
    );
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh] modal-panel-enter">
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Rekap Penjualan Kasir
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Rekapitulasi penjualan shift kasir berdasarkan produk terlaris
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-red-50 text-red-500 rounded hover:bg-red-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="h-full min-h-[240px] flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
              <p className="text-sm text-gray-500">Memuat rekap penjualan...</p>
            </div>
          )}

          {!loading && error && (
            <div className="h-full min-h-[240px] flex flex-col items-center justify-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <p className="text-sm text-red-600 text-center">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 text-sm font-medium text-gray-600">
                  <th className="py-3 px-4 rounded-l-lg">Nama Produk</th>
                  <th className="py-3 px-4 text-center">Terjual</th>
                  <th className="py-3 px-4 text-right rounded-r-lg">
                    Pendapatan
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="py-4 px-4 text-sm text-gray-700">
                      {item.name}
                    </td>
                    <td className="py-4 px-4 text-sm text-center text-gray-700">
                      {Number(item.sold || 0).toLocaleString("id-ID")}
                    </td>
                    <td className="py-4 px-4 text-sm text-right text-gray-700">
                      {formatCurrency(item.revenue)}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      className="py-10 text-center text-sm text-gray-500">
                      Belum ada data produk terlaris pada shift ini.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-6 border-t flex justify-end">
          <button
            onClick={handleDownload}
            disabled={loading || !!error || items.length === 0}
            className="px-6 py-2.5 bg-teal-50 text-teal-600 rounded-lg font-medium hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            <ArrowDown className="w-4 h-4" />
            Download
          </button>
        </div>
      </div>
    </div>
  );
}
